import React, { useRef, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, AppBar, Toolbar, Typography, Box, Alert, Button, Avatar, IconButton } from '@mui/material';
import { CloudQueue, Logout, Dashboard, AdminPanelSettings, Block } from '@mui/icons-material';
import DocumentFilter from './components/DocumentFilter';
import DocumentSearch from './components/DocumentSearch';
import DocumentList from './components/DocumentList';
import AdminPanel from './components/AdminPanel';
import { authService } from './services/api';

const theme = createTheme({
  palette: {
    primary: {
      main: '#A333C8',
      dark: '#3B1F5B',
    },
    secondary: {
      main: '#C2185B',
    },
    background: {
      default: '#E0E0E0'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700
    },
    h6: {
      fontWeight: 600
    }
  },
  shape: {
    borderRadius: 16
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 12
        }
      }
    }
  }
});

function LoginPage() {
  const handleLogin = async () => {
    try {
      const { authUrl } = await authService.getAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1E1E1E 0%, #3B1F5B 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box 
          sx={{
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '32px',
            p: 6,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 3,
              background: 'linear-gradient(135deg, #A333C8 0%, #3B1F5B 100%)',
              boxShadow: '0 8px 32px rgba(163, 51, 200, 0.3)'
            }}
          >
            <CloudQueue sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography 
            variant="h4" 
            gutterBottom
            sx={{
              background: 'linear-gradient(45deg, #A333C8 30%, #3B1F5B 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            AWS Case Studies
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, fontWeight: 400 }}>
            Search and Download AWS Case Studies
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            onClick={handleLogin}
            sx={{
              background: 'linear-gradient(45deg, #A333C8 30%, #C2185B 90%)',
              boxShadow: '0 8px 32px rgba(163, 51, 200, 0.3)',
              px: 4,
              py: 1.5,
              fontSize: '16px',
              borderRadius: '16px',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(163, 51, 200, 0.4)'
              }
            }}
          >
            Sign in with Wipro ADID
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

function UnauthorizedPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1E1E1E 0%, #3B1F5B 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Container maxWidth="sm">
        <Box 
          sx={{
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '32px',
            p: 6,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(194, 24, 91, 0.2)'
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 3,
              background: 'linear-gradient(135deg, #C2185B 0%, #880E4F 100%)',
              boxShadow: '0 8px 32px rgba(194, 24, 91, 0.3)'
            }}
          >
            <Block sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography 
            variant="h4" 
            gutterBottom
            sx={{
              color: '#C2185B',
              fontWeight: 700,
              mb: 2
            }}
          >
            Access Denied
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 400 }}>
            You are not authorized to access this application.
          </Typography>
          {email && (
            <Typography variant="body1" sx={{ mb: 3, color: '#7B68AE', fontWeight: 500 }}>
              {email}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Please reach out to AWS Practice for access.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.href = '/login'}
            sx={{
              background: 'linear-gradient(45deg, #3B1F5B 30%, #A333C8 90%)',
              boxShadow: '0 8px 32px rgba(163, 51, 200, 0.3)',
              px: 4,
              py: 1.5,
              borderRadius: '16px',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(163, 51, 200, 0.4)'
              }
            }}
          >
            Back to Login
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

function CallbackPage() {
  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        try {
          await authService.handleCallback(code);
          window.location.href = '/';
        } catch (error) {
          if (error.unauthorized) {
            window.location.href = `/unauthorized?email=${encodeURIComponent(error.email)}`;
          } else {
            console.error('Callback error:', error);
            window.location.href = '/login';
          }
        }
      } else {
        window.location.href = '/login';
      }
    };
    
    handleCallback();
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1E1E1E 0%, #3B1F5B 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Container maxWidth="sm">
        <Box 
          sx={{
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            p: 4,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Processing authentication...
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

function MainApp() {
  const documentListRef = useRef();
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [isAdmin, setIsAdmin] = useState(authService.isAdmin());
  const [allTags, setAllTags] = useState([]);
  const [showAdmin, setShowAdmin] = useState(false);

  const handleSearch = (searchTerm) => {
    if (documentListRef.current) {
      documentListRef.current.searchDocuments(searchTerm);
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
  };

  const handleReset = () => {
    if (documentListRef.current) {
      documentListRef.current.refreshDocuments();
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#E0E0E0' }}>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #000000 0%, #3B1F5B 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(163, 51, 200, 0.3)'
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Avatar
            sx={{
              mr: 2,
              background: 'rgba(163, 51, 200, 0.3)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Dashboard />
          </Avatar>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 700,
              background: 'linear-gradient(45deg, #fff 30%, #E0E0E0 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            AWS Success Stories
          </Typography>
          {isAdmin && (
          <IconButton 
            color="inherit" 
            onClick={() => setShowAdmin(!showAdmin)}
            sx={{
              mr: 1,
              background: showAdmin ? 'rgba(163, 51, 200, 0.4)' : 'rgba(163, 51, 200, 0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                background: 'rgba(163, 51, 200, 0.4)'
              }
            }}
          >
            <AdminPanelSettings />
          </IconButton>
          )}
          <IconButton 
            color="inherit" 
            onClick={handleLogout}
            sx={{
              background: 'rgba(163, 51, 200, 0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                background: 'rgba(163, 51, 200, 0.4)'
              }
            }}
          >
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {showAdmin && isAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
        <DocumentSearch 
          onSearch={handleSearch}
          onReset={handleReset}
        />
        <DocumentList ref={documentListRef} />
      </Container>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/auth/callback" element={<CallbackPage />} />
          <Route path="/" element={<MainApp />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
